import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { get, merge } from 'lodash';
import {
  TestSetupComponent,
  ITestSetupEntry,
  BaseSetupTab,
  TestSetupTabsComponent,
  SaveTemplateComponent,
  TestSetupModule,
  TemplateService
} from '@nrwl/test-setup';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import {
  DialogService,
  NotificationService,
  RunTypeValue,
  TestLayerValue,
  TestListService,
  TestMode,
  TestTypeValue,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { TemplateTabsComponent } from '@nrwl/template-management';
import { Y1564SetupService } from './y1564-setup.service';
import { Y1564SetupComponent } from './tabs/y1564-setup/y1564-setup.component';
import { Y1564StreamsComponent } from './tabs/y1564-streams/y1564-streams.component';
import { Y1564EndpointsComponent } from './tabs/y1564-endpoints/y1564-endpoints.component';
import {
  Y1564ConfigFieldMapping as ConfigFieldMapping,
  TOO_MANY_STREAMS_ERROR_MSG,
  vlanConfigValue
} from './models/models';

@Component({
  selector: 'nrwl-y1564-setup',
  standalone: true,
  imports: [TestSetupModule],
  providers: [Y1564SetupService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class Y1564TestSetupComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy, ITestSetupEntry
{
  @ViewChild(TestSetupTabsComponent, { static: true }) testSetupTabRef: TestSetupTabsComponent;
  templateDialogRef: MatDialogRef<TemplateTabsComponent>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupConfig: any;
  agentBasedConfig: any;
  setupId: string;
  mode: string;
  templateName: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  setupService: Y1564SetupService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  activatedRouteSubscription: Subscription;
  editDeviceDialogRef: MatDialogRef<SaveTemplateComponent>;
  currentObj: any; //eslint-disable-line
  templateId: string;
  testId = null;
  constructor(
    router: Router,
    setupService: Y1564SetupService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationProvider: NavigationProvider,
    private readonly dialogService: DialogService,
    private dialog: MatDialog,
    private readonly testListService: TestListService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly notificationService: NotificationService,
    private readonly templateService: TemplateService
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.currentObj = this;
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      if (this.testId !== this.setupId) {
        const defaultParams = merge(
          {},
          this.dataId,
          { modeType: this.mode },
          JSON.parse(get(data, 'defaultParams', '{}'))
        );
        this.templateId = defaultParams?.templateId;
        this.setupService.openTestSetup(
          this.setupId,
          this.setupConfig,
          defaultParams,
          null,
          null,
          this.agentBasedConfig
        );
        this.testId = this.setupId;
      }
    });
    if (!this.setupService.editMode) {
      this.setupService.currentConfig.buttonName = 'START TEST';
    }
    this.templateService.closeDialog?.subscribe(status => {
      if (status && this.templateDialogRef) {
        this.templateDialogRef.close();
      }
    });
  }

  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.setupService.reOpen();
  }

  openTestRunning(testId: string): void {
    this.navigationProvider.navigateToDashboard(['test-running'], {
      queryParams: {
        testId: testId,
        testName: this.setupService.output.alias,
        testType: TestTypeValue.Y1564,
        source: null,
        layeredSource: [],
        queryParamsHandling: 'merge',
        replaceUrl: true,
        hideTestSetup: true // to hide the testsetup wizard panel
      }
    });
  }
  downloadTestConfig(): void {
    this.setupService.onNext();
    const allTestParams = this.setupService.getAllTestParams();
    const lmapObject = this.setupService.createLMAPObject(allTestParams);
    this.setupService._downloadTestConfig(lmapObject);
  }
  saveTestConfig(): void {
    this.templateService.setTemplateId(this.templateId);
    this.setupService.onNext();
    let configParams = this.setupService._getConfigParameters();
    const otherParams = {
      [ConfigFieldMapping.SCHEDULER]: this.setupService.output[ConfigFieldMapping.SCHEDULER]
    };
    configParams = { ...configParams, ...otherParams };
    const allTestParams = this.setupService._createTemplateFormat(
      configParams,
      this.setupService.selectedOriginator,
      this.setupService.selectedDestination
    );
    allTestParams['group'] = this.setupService.getStreamsGroup('other');
    allTestParams['engine-a-info'] = this.setupService.getEngineAInfo();
    allTestParams['engine-b-info'] = this.setupService.getEngineBInfo();
    this.editDeviceDialogRef = this.dialog.open(SaveTemplateComponent, {
      width: '70rem',
      disableClose: true,
      data: { deviceInfo: allTestParams, editMode: true, testType: TestTypeValue.Y1564 }
    });
  }

  openTemplates(): void {
    const param = {
      id: 'setupService.test_templates',
      callBackService: this,
      callBackFuntion: this.openTemplatesDialog
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }
  // eslint-disable-next-line
  openTemplatesDialog(service: any): void {
    service.toggleClose();

    service.templateDialogRef = service.dialog.open(TemplateTabsComponent, {
      width: '100rem',
      minHeight: '50rem',
      height: 'calc(100vh - 20px)',
      disableClose: true,
      data: {
        filterType: TestTypeValue.Y1564,
        myTemplatesColumnHeaders: ['name', 'accessLevel', 'lastModified', 'selectActions'],
        sharedTemplatesColumnHeaders: [
          'name',
          'accessLevel',
          'owner',
          'lastModified',
          'selectActions'
        ]
      }
    });
  }
  toggleFinish(): void {
    this.createY1564Test();
  }

  createY1564Test(): void {
    const testMode = this.setupService.output[ConfigFieldMapping.TEST_MODE];
    const SelectedLayer = this.setupService.output[ConfigFieldMapping.LAYER_TO_TEST];
    const selectedVlan = this.setupService.output[ConfigFieldMapping.VLAN_CONFIG];
    const testPointModel =
      this.setupService.output[ConfigFieldMapping.SELECTED_ORIGINATOR]?.deviceData?.model;
    this.setupService.onNext();
    const allTestParams = this.setupService.getAllTestParams();
    const streamsLength = this.setupService.output[ConfigFieldMapping.STREAMS]?.length;
    if (
      testMode === TestMode.LOOPBACK &&
      SelectedLayer === TestLayerValue.L2 &&
      selectedVlan === vlanConfigValue.UNTAGGED &&
      streamsLength > 1 &&
      testPointModel?.model.includes('JMEP')
    ) {
      this.dialogService.showInfo(
        'information',
        'body',
        TOO_MANY_STREAMS_ERROR_MSG,
        'Too Many Streams Defined'
      );
    } else {
      this.setupService.openTestStatusDialog(
        this.setupService.editMode ? 'Updating y1564' : 'Creating y1564',
        this.setupService.editMode ? 'Update' : 'Add'
      );
      const lmapObject = this.setupService.createLMAPObject(allTestParams);
      if (!this.setupService.editMode) {
        this.subscription = this.setupService._saveControllerTest(lmapObject).subscribe(
          response => {
            const message = 'Y1564 test has been created';
            this.notificationService.onSuccess(message);
            this.testListService.updateTestResponse();
            this.setupService.dialogRef.close();
            this.toggleClose();
            this.exchange.notifyListeners('new_test', response);
            if (this.setupService.output?.runType === RunTypeValue.ON_DEMAND) {
              this.openTestRunning(response.data.id);
            }
          },
          error => {
            const message = error?.error?.message
              ? 'Failed to create y1564 test. Reason:' + error.error?.message
              : 'Failed to create y1564 test';
            this.notificationService.onFail(message);
            this.setupService.dialogRef.close();
            this.toggleClose();
          }
        );
      } else {
        this.subscription = this.setupService
          ._updateControllerTest({
            opt: { id: this.setupService.defaultParamsById['scheduleId'], apply: 'next-run' },
            config: lmapObject
          })
          .subscribe(
            response => {
              const message = 'Y1564 test has been updated';
              this.notificationService.onSuccess(message);
              this.testListService.updateTestResponse();
              this.setupService.dialogRef.close();
              this.toggleClose();
              this.exchange.notifyListeners('new_test', response);
            },
            error => {
              const message = error?.error?.message
                ? 'Failed to update y1564 test. Reason: ' + error.error?.message
                : 'Failed to update y1564 test';
              this.notificationService.onFail(message);
              this.setupService.dialogRef.close();
              this.toggleClose();
            }
          );
      }
    }
  }

  getSetupService(): Y1564SetupService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'y1564.setup.html':
        return Y1564SetupComponent;
      case 'y1564.endpoints.html':
        return Y1564EndpointsComponent;
      case 'y1564.streams.html':
        return Y1564StreamsComponent;
      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }
}
